/**
 * useScrollIsolation
 * Lets an overflow container scroll when it lives inside a Radix Dialog/Drawer.
 *
 * Radix's modal scroll lock (react-remove-scroll) attaches a non-passive
 * `wheel`/`touchmove` listener on `document` (bubble phase) and preventDefault()s
 * any event whose target sits outside the dialog's lock node. A Popover portaled
 * to `document.body` is outside that node, so its lists silently refuse to
 * scroll. Stopping the event from bubbling to `document` from inside the
 * container makes the lock's handler skip it, restoring native scrolling.
 *
 * Attachment MUST ride a callback ref: Radix mounts portal content a tick
 * after open flips, so an effect keyed on `open` runs while the ref is still
 * null and never attaches (the original shape of this hook — a silent no-op).
 * The returned callback doubles as the element ref and mirrors the node into
 * the caller's RefObject for its other uses (keyboard scrollIntoView etc).
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { useCallback, useRef } from 'react';

export function useScrollIsolation<T extends HTMLElement>(ref: {
  current: T | null;
}): (node: T | null) => void {
  const cleanupRef = useRef<(() => void) | null>(null);
  return useCallback(
    (node: T | null) => {
      ref.current = node;
      cleanupRef.current?.();
      cleanupRef.current = null;
      if (!node) return;
      const stop = (event: Event) => event.stopPropagation();
      node.addEventListener('wheel', stop, { passive: true });
      node.addEventListener('touchmove', stop, { passive: true });
      cleanupRef.current = () => {
        node.removeEventListener('wheel', stop);
        node.removeEventListener('touchmove', stop);
      };
    },
    [ref],
  );
}

export default useScrollIsolation;
